/**
 * Subscription Context
 * Provides subscription state and credit balance globally
 * @module contexts/SubscriptionContext
 */

import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { getCreditsBucket, updateUserProperties } from '../services/analytics.service';
import { getCurrentUser, getIdToken, isAuthenticated } from '../services/cognitoService';
import {
  type CreditBalance,
  checkAndSyncSubscription,
  checkoutStripeCredits,
  checkoutStripeSubscription,
  checkPlatformConflict,
  clearRevenueCatUser,
  getCreditBalance,
  initializeRevenueCat,
  type PlatformConflict,
  type SubscriptionStatus,
} from '../services/revenuecatService';
import logger from '../utils/logger';

// =============================================================================
// TYPES
// =============================================================================

interface SubscriptionContextType {
  // Subscription state
  subscription: SubscriptionStatus | null;
  credits: CreditBalance | null;
  isLoading: boolean;
  error: string | null;

  // Platform conflict state (cross-platform guard)
  platformConflict: PlatformConflict | null;
  isConflictLoading: boolean;

  // Welcome credits toast (shown once on first login)
  showWelcomeCreditsToast: boolean;
  dismissWelcomeCreditsToast: () => void;

  // Actions
  refreshSubscription: () => Promise<void>;
  purchasePlan: (planId: 'basic_monthly' | 'plus_monthly' | 'premium_monthly') => Promise<boolean>;
  purchaseCredits: (
    packId: 'pack_25' | 'pack_50' | 'pack_75' | 'pack_125' | 'pack_150' | 'pack_250',
  ) => Promise<boolean>;

  // Helpers
  hasTier: (tier: 'basic' | 'plus' | 'premium') => boolean;
  isFree: () => boolean;
  hasActiveSubscription: () => boolean;
  canAccessFeature: (feature: 'live_avatar' | 'voice' | 'text') => boolean;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

// =============================================================================
// PROVIDER
// =============================================================================

interface SubscriptionProviderProps {
  children: React.ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [credits, setCredits] = useState<CreditBalance | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  // Platform conflict guard state
  const [platformConflict, setPlatformConflict] = useState<PlatformConflict | null>(null);
  const [isConflictLoading, setIsConflictLoading] = useState(false);

  // Welcome credits toast — shown once if this is user's first login
  const [showWelcomeCreditsToast, setShowWelcomeCreditsToast] = useState(false);

  const dismissWelcomeCreditsToast = useCallback(() => setShowWelcomeCreditsToast(false), []);

  // Helper to handle welcome credits toast check and async retries for new users
  const processWelcomeCreditsCheck = useCallback((balance: CreditBalance, userId: string) => {
    const toastKey = `welcomeToastShown:${userId}`;

    // Suppress welcome toast if user is returning from a purchase checkout redirect
    if (
      window.location.search.includes('session_id') ||
      window.location.search.includes('success=true')
    ) {
      localStorage.setItem(toastKey, 'true');
      return;
    }

    if (localStorage.getItem(toastKey)) {
      return;
    }

    if (balance.welcome_credits_granted) {
      setShowWelcomeCreditsToast(true);
      localStorage.setItem(toastKey, 'true');
    } else {
      // For newly registered users, backend user-provisioning Lambda runs asynchronously via EventBridge.
      // Retry checking credit balance after 2.5s and 6s to display welcome credits prompt as soon as granted.
      const retryCheck = async () => {
        if (localStorage.getItem(toastKey)) return;
        try {
          const freshToken = await getIdToken();
          if (freshToken) {
            const freshBalance = await getCreditBalance(freshToken);
            setCredits(freshBalance);
            if (freshBalance.welcome_credits_granted && !localStorage.getItem(toastKey)) {
              setShowWelcomeCreditsToast(true);
              localStorage.setItem(toastKey, 'true');
            }
          }
        } catch (_e) {
          // Retry errors ignored
        }
      };

      setTimeout(retryCheck, 2500);
      setTimeout(retryCheck, 6000);
    }
  }, []);

  // =============================================================================
  // INITIALIZATION
  // =============================================================================

  useEffect(() => {
    const initialize = async () => {
      // Check auth status - retry a few times if tokens aren't loaded yet
      let authenticated = isAuthenticated();

      if (!authenticated) {
        // Wait a bit and check again (tokens might be loading)
        await new Promise((resolve) => setTimeout(resolve, 100));
        authenticated = isAuthenticated();
      }

      setAuthChecked(true);

      if (!authenticated) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const currentUser = getCurrentUser();
        if (!currentUser?.userId) {
          setIsLoading(false);
          return;
        }
        const userId = currentUser.userId;

        // Initialize RevenueCat
        await initializeRevenueCat(userId);
        setIsInitialized(true);

        // Get auth token for backend calls
        const token = await getIdToken();
        if (!token) {
          throw new Error('Failed to get auth token');
        }

        // Check subscription and sync to backend
        const status = await checkAndSyncSubscription(token);
        setSubscription(status);

        // Check for cross-platform subscription conflict (web = requesting_platform)
        try {
          setIsConflictLoading(true);
          const conflict = await checkPlatformConflict(token, 'web');
          setPlatformConflict(conflict);
        } catch (conflictError) {
          logger.warn('Subscription', 'Failed to check platform conflict', conflictError);
          setPlatformConflict(null);
        } finally {
          setIsConflictLoading(false);
        }

        // Get credit balance
        try {
          const balance = await getCreditBalance(token);
          setCredits(balance);
          processWelcomeCreditsCheck(balance, userId);
          updateUserProperties({
            planType: status.tier || 'free',
            creditsBalanceBucket: getCreditsBucket(balance?.credits?.total),
          });
        } catch (creditError) {
          logger.warn('Subscription', 'Failed to get credits', creditError);
          updateUserProperties({
            planType: status.tier || 'free',
          });
        }
      } catch (err) {
        logger.error('Subscription', 'Initialization failed', err as Error);
        setError(err instanceof Error ? err.message : 'Failed to load subscription');
        // Set default free tier
        setSubscription({
          tier: 'free',
          isActive: false,
          expiresAt: null,
          entitlements: [],
          productId: null,
          store: null,
        });
      } finally {
        setIsLoading(false);
      }
    };

    initialize();

    // Also listen for storage changes (login/logout in another tab)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'accessToken' || e.key === 'idToken') {
        initialize();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearRevenueCatUser();
    };
  }, [processWelcomeCreditsCheck]);

  // Re-initialize if user becomes authenticated after initial check
  useEffect(() => {
    if (authChecked && !isInitialized && isAuthenticated()) {
      const reinitialize = async () => {
        const currentUser = getCurrentUser();
        if (!currentUser?.userId) return;

        try {
          setIsLoading(true);
          await initializeRevenueCat(currentUser.userId);
          setIsInitialized(true);

          const token = await getIdToken();
          if (token) {
            const status = await checkAndSyncSubscription(token);
            setSubscription(status);
            const balance = await getCreditBalance(token);
            setCredits(balance);
            processWelcomeCreditsCheck(balance, currentUser.userId);
            updateUserProperties({
              planType: status.tier || 'free',
              creditsBalanceBucket: getCreditsBucket(balance?.credits?.total),
            });
          }
        } catch (err) {
          logger.error('Subscription', 'Retry initialization failed', err as Error);
        } finally {
          setIsLoading(false);
        }
      };
      reinitialize();
    }
  }, [authChecked, isInitialized, processWelcomeCreditsCheck]);

  // =============================================================================
  // REFRESH
  // =============================================================================

  const refreshSubscription = useCallback(async () => {
    // If not initialized but user is authenticated, try to initialize first
    if (!isInitialized && isAuthenticated()) {
      const currentUser = getCurrentUser();
      if (currentUser?.userId) {
        try {
          await initializeRevenueCat(currentUser.userId);
          setIsInitialized(true);
        } catch (initErr) {
          logger.error('Subscription', 'Failed to initialize on refresh', initErr as Error);
          return;
        }
      }
    }

    if (!isInitialized && !isAuthenticated()) return;

    try {
      setError(null);

      const token = await getIdToken();
      if (!token) {
        throw new Error('Failed to get auth token');
      }

      // Refresh subscription status
      const status = await checkAndSyncSubscription(token);
      setSubscription(status);

      // Refresh credits
      try {
        const balance = await getCreditBalance(token);
        setCredits(balance);
        updateUserProperties({
          planType: status.tier || 'free',
          creditsBalanceBucket: getCreditsBucket(balance?.credits?.total),
        });
      } catch (creditError) {
        logger.warn('Subscription', 'Failed to refresh credits', creditError);
        updateUserProperties({
          planType: status.tier || 'free',
        });
      }
    } catch (err) {
      logger.error('Subscription', 'Refresh failed', err as Error);
      setError(err instanceof Error ? err.message : 'Failed to refresh subscription');
    }
  }, [isInitialized]);

  // =============================================================================
  // PURCHASES
  // =============================================================================

  const purchasePlan = useCallback(
    async (planId: 'basic_monthly' | 'plus_monthly' | 'premium_monthly'): Promise<boolean> => {
      try {
        setError(null);

        const currentUser = getCurrentUser();
        if (currentUser?.userId) {
          localStorage.setItem(`welcomeToastShown:${currentUser.userId}`, 'true');
        }

        const token = await getIdToken();
        if (!token) {
          throw new Error('Failed to get auth token');
        }

        // Redirect to native Stripe Checkout
        await checkoutStripeSubscription(planId, token);
        return true;
      } catch (err: unknown) {
        if ((err as Error)?.message === 'PURCHASE_CANCELLED') {
          return false;
        }
        logger.error('Subscription', 'Purchase failed', err as Error);
        setError(err instanceof Error ? err.message : 'Purchase failed');
        throw err;
      }
    },
    [],
  );

  const purchaseCredits = useCallback(
    async (
      packId: 'pack_25' | 'pack_50' | 'pack_75' | 'pack_125' | 'pack_150' | 'pack_250',
    ): Promise<boolean> => {
      try {
        setError(null);

        const currentUser = getCurrentUser();
        if (currentUser?.userId) {
          localStorage.setItem(`welcomeToastShown:${currentUser.userId}`, 'true');
        }

        // Get auth token before purchase (needed for backend sync)
        const token = await getIdToken();
        if (!token) {
          throw new Error('Failed to get auth token');
        }

        // Redirect to native Stripe Checkout
        await checkoutStripeCredits(packId, token);
        return true;

        /* 
        // --- PREVIOUS REVENUECAT FLOW ---
        // Purchase via RevenueCat - this also syncs to backend automatically
        await purchaseCreditPack(packId, token);

        // Refresh credits to show updated balance
        const balance = await getCreditBalance(token);
        setCredits(balance);

        return true;
        */
      } catch (err: unknown) {
        if ((err as Error)?.message === 'PURCHASE_CANCELLED') {
          return false;
        }
        logger.error('Subscription', 'Credit purchase failed', err as Error);
        setError(err instanceof Error ? err.message : 'Purchase failed');
        throw err;
      }
    },
    [],
  );

  // =============================================================================
  // HELPERS
  // =============================================================================

  const hasTier = useCallback(
    (tier: 'basic' | 'plus' | 'premium'): boolean => {
      if (!subscription) return false;

      // Tier hierarchy: premium > plus > basic > free
      if (tier === 'basic') {
        return ['basic', 'plus', 'premium'].includes(subscription.tier);
      }
      if (tier === 'plus') {
        return ['plus', 'premium'].includes(subscription.tier);
      }
      return subscription.tier === 'premium';
    },
    [subscription],
  );

  const isFree = useCallback((): boolean => {
    return !subscription || subscription.tier === 'free';
  }, [subscription]);

  const hasActiveSubscription = useCallback((): boolean => {
    return subscription?.isActive ?? false;
  }, [subscription]);

  const canAccessFeature = useCallback(
    (feature: 'live_avatar' | 'voice' | 'text'): boolean => {
      if (!subscription || !credits) return false;

      switch (feature) {
        case 'live_avatar':
          // Check if user has voice/video credits
          return credits.features.voice_video;

        case 'voice':
          // Check if user can access voice/video
          return credits.features.voice_video;

        case 'text':
          // Check if user can access text
          return credits.features.text;

        default:
          return false;
      }
    },
    [subscription, credits],
  );

  // =============================================================================
  // RENDER
  // =============================================================================

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        credits,
        isLoading,
        error,
        platformConflict,
        isConflictLoading,
        showWelcomeCreditsToast,
        dismissWelcomeCreditsToast,
        refreshSubscription,
        purchasePlan,
        purchaseCredits,
        hasTier,
        isFree,
        hasActiveSubscription,
        canAccessFeature,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

// =============================================================================
// HOOK
// =============================================================================

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};
